import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Button, Field, FormError, Input } from "../components/ui";
import { AuthLayout } from "../features/auth/auth-layout";
import { authClient } from "../lib/auth-client";
import { useTRPC } from "../lib/trpc";
import { safeRedirect } from "../lib/utils";

interface SignInSearch {
  redirect?: string;
  reason?: "expired";
  /** Set by Better Auth when a Google/Microsoft sign-in is refused. */
  error?: string;
}

export const Route = createFileRoute("/sign-in")({
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    ...(typeof search.redirect === "string" && { redirect: search.redirect }),
    ...(search.reason === "expired" && { reason: "expired" as const }),
    ...(typeof search.error === "string" && { error: search.error.slice(0, 100) }),
  }),
  component: SignIn,
});

const PROVIDER_LABELS = { google: "Google", microsoft: "Microsoft" } as const;

function SignIn() {
  const { redirect, reason, error: providerError } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const config = useQuery(trpc.system.config.queryOptions());
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);
  const target = safeRedirect(redirect);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setPending(true);
    const { data, error: signInError } = await authClient.signIn.email({ email, password });
    setPending(false);
    if (signInError) {
      setError(
        signInError.status === 429
          ? "Too many attempts. Wait a minute and try again."
          : signInError.status === 403 && signInError.message
            ? signInError.message
            : "Incorrect email or password.",
      );
      return;
    }
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) {
      await navigate({ to: "/two-factor", search: { redirect: target } });
      return;
    }
    queryClient.clear();
    await navigate({ to: target });
  };

  const social = async (provider: "google" | "microsoft") => {
    const { error: socialError } = await authClient.signIn.social({
      provider,
      callbackURL: target,
      errorCallbackURL: "/sign-in",
    });
    if (socialError)
      setError(socialError.message ?? "That sign-in method isn't available right now.");
  };

  return (
    <AuthLayout title="Sign in" description="Welcome back. Sign in to your workspace.">
      {providerError && (
        <p role="alert" className="mb-4 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          We couldn't sign you in with that provider. If your account uses two-factor
          authentication, sign in with your email, password and authenticator code instead. Accounts
          are invitation-only.
        </p>
      )}
      {reason === "expired" && (
        <output className="mb-4 block rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">
          Your session expired. Please sign in again.
        </output>
      )}
      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
        <Field label="Email">
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <FormError message={error} />
        <Button type="submit" loading={pending} disabled={!email || !password}>
          Sign in
        </Button>
        <Link to="/forgot-password" className="text-center text-sm text-primary hover:underline">
          Forgot your password?
        </Link>
      </form>
      {config.data && config.data.socialProviders.length > 0 && (
        <div className="mt-6 flex flex-col gap-2 border-t border-border pt-6">
          {config.data.socialProviders.map((p) => (
            <Button key={p} variant="secondary" onClick={() => void social(p)}>
              Continue with {PROVIDER_LABELS[p]}
            </Button>
          ))}
        </div>
      )}
    </AuthLayout>
  );
}
